/**
 * Phase 3 Test Panel
 *
 * Admin-only component for running Phase 3 integration tests.
 * Tests the complete game loop: actions, tick processor, elections.
 */

import { useState, useCallback, useRef } from 'react'
import {
  Play,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  AlertCircle,
  Trash2,
  Timer,
  Bug,
  Zap,
} from 'lucide-react'
import { AdminOnly } from '@/components/AdminOnly'
import {
  runPhase3TestsByCategory,
  type Phase3TestSuite,
  type Phase3TestConfig,
} from '@/lib/testing/phase3Tests'
import type { TestCategory, TestResult } from '@/lib/testing/phase2Tests'
import { cleanupTestWorlds } from '@/lib/testing/testSetup'

export function Phase3TestPanel() {
  const [testSuite, setTestSuite] = useState<Phase3TestSuite | null>(null)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [isRunning, setIsRunning] = useState(false)
  const [isCleaning, setIsCleaning] = useState(false)
  const [progress, setProgress] = useState<{ message: string; detail?: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [runFullSim, setRunFullSim] = useState(false)
  const [runMultiParty, setRunMultiParty] = useState(false)
  const abortRef = useRef(false)

  const handleRunTests = useCallback(async () => {
    setIsRunning(true)
    setError(null)
    setTestSuite(null)
    abortRef.current = false

    try {
      const config: Phase3TestConfig = {
        partyCount: 4,
        runFullSimulation: runFullSim,
        runMultiPartyScenarios: runMultiParty,
        onProgress: (message, detail) => {
          setProgress({ message, detail })
        },
      }

      const result = await runPhase3TestsByCategory(config)
      setTestSuite(result)

      // Expand categories with failures
      const failedCategories = result.categories
        .filter(c => c.failed > 0)
        .map(c => c.name)
      setExpandedCategories(
        new Set(failedCategories.length > 0 ? failedCategories : result.categories.map(c => c.name))
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setIsRunning(false)
      setProgress(null)
    }
  }, [runFullSim, runMultiParty])

  const handleCleanup = useCallback(async () => {
    setIsCleaning(true)
    try {
      const result = await cleanupTestWorlds()
      if (result.errors.length > 0) {
        setError(`Cleanup: ${result.errors.join(', ')}`)
      } else {
        setError(null)
      }
      // Clear test results after cleanup
      if (result.deletedWorlds > 0) {
        setTestSuite(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setIsCleaning(false)
    }
  }, [])

  const toggleCategory = (name: string) => {
    const next = new Set(expandedCategories)
    if (next.has(name)) {
      next.delete(name)
    } else {
      next.add(name)
    }
    setExpandedCategories(next)
  }

  return (
    <AdminOnly fallback={<div className="p-4 text-slate-400">Admin access required</div>}>
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        {/* Header */}
        <div className="p-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Phase 3 Integration Tests</h2>
          <p className="text-sm text-slate-400 mt-1">
            Test the complete game loop: actions, tick processor, elections, and edge cases.
          </p>
        </div>

        {/* Controls */}
        <div className="p-4 border-b border-slate-700 space-y-3">
          {/* Options */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={runFullSim}
                onChange={(e) => setRunFullSim(e.target.checked)}
                disabled={isRunning}
                className="rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
              />
              <Timer className="h-4 w-4 text-slate-400" />
              16-Tick Simulation
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={runMultiParty}
                onChange={(e) => setRunMultiParty(e.target.checked)}
                disabled={isRunning}
                className="rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
              />
              <Zap className="h-4 w-4 text-slate-400" />
              Multi-Party Scenarios (1, 2, 4, 6)
            </label>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleRunTests}
              disabled={isRunning || isCleaning}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors"
            >
              {isRunning ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Run Phase 3 Tests
                </>
              )}
            </button>

            <button
              onClick={handleCleanup}
              disabled={isRunning || isCleaning}
              className="flex items-center gap-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-600/30 text-red-400 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
            >
              {isCleaning ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cleaning...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Cleanup Test Data
                </>
              )}
            </button>

            {testSuite && (
              <button
                onClick={() => {
                  setTestSuite(null)
                  setExpandedCategories(new Set())
                }}
                className="flex items-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-md text-sm font-medium transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Progress */}
        {isRunning && progress && (
          <div className="px-4 py-3 border-b border-slate-700 bg-blue-900/10">
            <div className="flex items-center gap-2 text-blue-400 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="font-medium">{progress.message}</span>
              {progress.detail && (
                <span className="text-blue-400/70">- {progress.detail}</span>
              )}
            </div>
          </div>
        )}

        {/* Summary */}
        {testSuite && (
          <div className="p-4 border-b border-slate-700 flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-green-400 font-medium">{testSuite.totalPassed} passed</span>
            </div>
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <span className="text-red-400 font-medium">{testSuite.totalFailed} failed</span>
            </div>
            <div className="text-slate-400 text-sm">
              {testSuite.totalTests} tests in {(testSuite.duration / 1000).toFixed(1)}s
            </div>

            {testSuite.totalFailed === 0 && testSuite.totalTests > 0 && (
              <div className="ml-auto flex items-center gap-2 text-green-400">
                <CheckCircle className="h-5 w-5" />
                All tests passing!
              </div>
            )}

            {testSuite.bugs.length > 0 && (
              <div className="ml-auto flex items-center gap-2 text-amber-400">
                <Bug className="h-5 w-5" />
                {testSuite.bugs.length} bug(s) found
              </div>
            )}
          </div>
        )}

        {/* Performance Summary */}
        {testSuite && testSuite.tickPerformance && testSuite.tickPerformance.length > 0 && (
          <div className="px-4 py-3 border-b border-slate-700 bg-slate-800/50">
            <div className="text-sm text-slate-400">
              <span className="font-medium text-slate-300">Performance: </span>
              {testSuite.tickPerformance.length} ticks,
              avg {(testSuite.tickPerformance.reduce((s, t) => s + t.durationMs, 0) / testSuite.tickPerformance.length).toFixed(0)}ms,
              max {Math.max(...testSuite.tickPerformance.map(t => t.durationMs))}ms
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="p-4 border-b border-slate-700 bg-red-900/20">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="h-5 w-5" />
              <span className="text-sm">{error}</span>
            </div>
          </div>
        )}

        {/* Test Results by Category */}
        {testSuite && (
          <div className="divide-y divide-slate-700">
            {testSuite.categories.map((category) => (
              <TestCategorySection
                key={category.name}
                category={category}
                isExpanded={expandedCategories.has(category.name)}
                onToggle={() => toggleCategory(category.name)}
              />
            ))}
          </div>
        )}

        {/* Bug Report */}
        {testSuite && testSuite.bugs.length > 0 && (
          <div className="p-4 border-t border-slate-700">
            <h3 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-2">
              <Bug className="h-4 w-4" />
              Bug Report ({testSuite.bugs.length})
            </h3>
            <div className="space-y-2">
              {testSuite.bugs.map((bug) => (
                <div
                  key={bug.id}
                  className={`rounded-md p-3 text-sm ${
                    bug.severity === 'high'
                      ? 'bg-red-900/20 border border-red-800/30'
                      : bug.severity === 'medium'
                      ? 'bg-amber-900/20 border border-amber-800/30'
                      : 'bg-slate-700/30 border border-slate-600/30'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded ${
                        bug.severity === 'high'
                          ? 'bg-red-600/20 text-red-400'
                          : bug.severity === 'medium'
                          ? 'bg-amber-600/20 text-amber-400'
                          : 'bg-slate-600/20 text-slate-400'
                      }`}
                    >
                      {bug.severity}
                    </span>
                    <span className="font-medium text-white">{bug.id}: {bug.title}</span>
                  </div>
                  <p className="text-slate-400 mt-1">{bug.description}</p>
                  <p className="text-slate-500 text-xs mt-1">Component: {bug.component}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!testSuite && !isRunning && (
          <div className="p-8 text-center text-slate-400">
            <Play className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Click "Run Phase 3 Tests" to begin integration testing.</p>
            <p className="text-xs mt-2 text-slate-500">
              Tests create isolated test worlds and clean up after themselves.
            </p>
          </div>
        )}
      </div>
    </AdminOnly>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function TestCategorySection({
  category,
  isExpanded,
  onToggle,
}: {
  category: TestCategory
  isExpanded: boolean
  onToggle: () => void
}) {
  const allPassed = category.failed === 0

  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-3 hover:bg-slate-700/50 transition-colors text-left"
      >
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 text-slate-400" />
        ) : (
          <ChevronRight className="h-5 w-5 text-slate-400" />
        )}
        <span className="font-medium text-white flex-1">{category.name}</span>
        <span className={`text-sm font-medium ${allPassed ? 'text-green-400' : 'text-red-400'}`}>
          {category.passed}/{category.tests.length} passed
        </span>
        {allPassed ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : (
          <XCircle className="h-5 w-5 text-red-500" />
        )}
      </button>

      {isExpanded && (
        <div className="pb-4 px-4">
          <div className="ml-8 space-y-2">
            {category.tests.map((test, index) => (
              <TestResultItem key={`${test.name}-${index}`} result={test} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function TestResultItem({ result }: { result: TestResult }) {
  const [showErrors, setShowErrors] = useState(false)

  return (
    <div className={`rounded-md p-3 ${result.passed ? 'bg-slate-700/30' : 'bg-red-900/20'}`}>
      <div className="flex items-start gap-3">
        {result.passed ? (
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
        ) : (
          <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <span className={`font-medium ${result.passed ? 'text-slate-200' : 'text-red-300'}`}>
            {result.name}
          </span>
          <p className="text-sm text-slate-400 mt-1">{result.details}</p>

          {result.errors && result.errors.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => setShowErrors(!showErrors)}
                className="text-sm text-red-400 hover:text-red-300 flex items-center gap-1"
              >
                {showErrors ? (
                  <><ChevronDown className="h-4 w-4" /> Hide errors ({result.errors.length})</>
                ) : (
                  <><ChevronRight className="h-4 w-4" /> Show errors ({result.errors.length})</>
                )}
              </button>
              {showErrors && (
                <ul className="mt-2 space-y-1 text-sm text-red-400 bg-red-900/30 rounded p-2">
                  {result.errors.map((err, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-red-500">•</span>
                      <span className="break-words">{err}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Phase3TestPanel
